import { AudioManager } from "../core/audio";
import { GameStorage, type BestRecord, type StorageLike } from "../core/storage";
import {
  PAIRS_TO_CLEAR,
  chooseCard,
  continueAfterFeedback,
  createGame,
  type GameState,
} from "../games/futatsu-de-pittari/game";
import {
  DIFFICULTIES,
  DIFFICULTY_ORDER,
  GAME_MANIFEST,
  type DifficultyId,
} from "../games/futatsu-de-pittari/manifest";

type ScreenName = "home" | "game" | "result" | "settings";

interface ResultState {
  readonly difficultyId: DifficultyId;
  readonly elapsedMs: number;
  readonly mistakeCount: number;
  readonly isNewBest: boolean;
}

function requiredElement<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Missing element: ${selector}`);
  return element;
}

function formatTime(elapsedMs: number): string {
  return `${(elapsedMs / 1000).toFixed(1)}びょう`;
}

function formatRunningTime(elapsedMs: number): string {
  const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = String(totalSeconds % 60).padStart(2, "0");
  return `${minutes}:${seconds}`;
}

function formatBestRecord(record?: BestRecord): string {
  if (!record) return "ベスト なし";
  return `ミス ${record.mistakeCount}かい・${formatTime(record.elapsedMs)}`;
}

export class GameApp {
  private readonly screens: Record<ScreenName, HTMLElement> = {
    home: requiredElement("#home-screen"),
    game: requiredElement("#game-screen"),
    result: requiredElement("#result-screen"),
    settings: requiredElement("#settings-screen"),
  };

  private readonly confirmDialog = requiredElement<HTMLDialogElement>("#confirm-dialog");
  private readonly confirmTitle = requiredElement<HTMLElement>("#confirm-title");
  private readonly confirmMessage = requiredElement<HTMLElement>("#confirm-message");
  private readonly confirmAccept = requiredElement<HTMLButtonElement>("#confirm-accept");
  private readonly confirmCancel = requiredElement<HTMLButtonElement>("#confirm-cancel");
  private readonly toast = requiredElement<HTMLElement>("#toast");
  private readonly storage: GameStorage;
  private readonly audio: AudioManager;

  private game: GameState | null = null;
  private result: ResultState | null = null;
  private startedAt = 0;
  private inputLocked = false;
  private showNewRound = false;
  private sessionId = 0;
  private timerId: number | null = null;
  private pendingConfirmation: (() => void) | null = null;
  private toastTimer: number | null = null;

  public constructor(storage?: StorageLike) {
    this.storage = new GameStorage(storage);
    this.audio = new AudioManager(this.storage.getSnapshot().soundEnabled);
    this.bindConfirmationDialog();
    this.renderHome();
    this.showScreen("home");
  }

  private bindConfirmationDialog(): void {
    this.confirmCancel.addEventListener("click", () => this.closeConfirmation());
    this.confirmAccept.addEventListener("click", () => {
      const action = this.pendingConfirmation;
      this.closeConfirmation();
      action?.();
    });
    this.confirmDialog.addEventListener("cancel", () => {
      this.pendingConfirmation = null;
    });
  }

  private showScreen(name: ScreenName): void {
    for (const [screenName, element] of Object.entries(this.screens)) {
      element.classList.toggle("is-hidden", screenName !== name);
    }
    window.scrollTo({ top: 0, behavior: "instant" });
  }

  private renderHome(): void {
    const records = this.storage.getSnapshot().bestRecords;
    const difficultyButtons = DIFFICULTY_ORDER.map((difficultyId) => {
      const difficulty = DIFFICULTIES[difficultyId];
      return `
        <button
          class="difficulty-button"
          type="button"
          data-difficulty="${difficulty.id}"
          aria-label="${difficulty.label}、${difficulty.minTarget}から${difficulty.maxTarget}、カード${difficulty.boardSize}まい、10もんタイムアタック"
        >
          <span class="difficulty-icon" aria-hidden="true">${difficulty.icon}</span>
          <span class="difficulty-copy">
            <span class="difficulty-name">${difficulty.label}</span>
            <span class="difficulty-range">${difficulty.minTarget} から ${difficulty.maxTarget}・${difficulty.boardSize}まい</span>
            <span class="difficulty-best">${formatBestRecord(records[difficultyId])}</span>
          </span>
          <span class="difficulty-arrow" aria-hidden="true">›</span>
        </button>
      `;
    }).join("");

    this.screens.home.innerHTML = `
      <div class="home-screen-content">
        <p class="eyebrow">${GAME_MANIFEST.category}さがし パズル</p>
        <div class="title-lockup">
          <h1 id="game-title">ふたつで<br><span class="title-highlight">ぴったり</span></h1>
        </div>
        <p class="subtitle">ふたつの かずを たして つくろう！</p>
        <p class="time-attack-badge"><span aria-hidden="true">⏱</span> 10もん タイムアタック</p>
        <div class="mini-equation" aria-hidden="true">
          <span class="mini-number">3</span><span class="math-sign">＋</span>
          <span class="mini-number">5</span><span class="math-sign">＝</span>
          <span class="mini-answer">8</span>
        </div>
        <p class="section-label">むずかしさを えらんでね</p>
        <div class="difficulty-list">${difficultyButtons}</div>
        <button class="text-button" id="open-settings" type="button">？ あそびかた・せってい</button>
        <a class="collection-link" href="./games.html">ゲームいちらんへ</a>
      </div>
    `;

    this.screens.home.querySelectorAll<HTMLButtonElement>("[data-difficulty]").forEach((button) => {
      button.addEventListener("click", () => {
        const difficultyId = button.dataset.difficulty as DifficultyId;
        this.startGame(difficultyId);
      });
    });
    requiredElement<HTMLButtonElement>("#open-settings").addEventListener("click", () => {
      this.renderSettings();
      this.showScreen("settings");
    });
  }

  private startGame(difficultyId: DifficultyId): void {
    this.sessionId += 1;
    this.game = createGame(difficultyId);
    this.result = null;
    this.inputLocked = false;
    this.showNewRound = false;
    this.startedAt = performance.now();
    this.renderGame();
    this.showScreen("game");
    this.startTimer();
  }

  private renderGame(): void {
    if (!this.game) return;
    const game = this.game;
    const difficulty = DIFFICULTIES[game.difficultyId];
    const progress = (game.correctCount / PAIRS_TO_CLEAR) * 100;
    const selectedValue = game.selectedIndex === null ? null : game.cards[game.selectedIndex];

    let equation = selectedValue === null
      ? `？ ＋ ？ ＝ ${game.target}`
      : `${selectedValue} ＋ ？ ＝ ${game.target}`;
    let feedbackText = "カードを ふたつ えらんでね";
    let feedbackClass = "is-hint";

    if (selectedValue !== null) {
      feedbackText = `✓ 1まいめ「${selectedValue}」を えらんだよ`;
      feedbackClass = "is-selection";
    }

    if (this.showNewRound) {
      feedbackText = "↻ つぎの もんだい！";
      feedbackClass = "is-next";
    }

    if (game.feedback) {
      const [firstIndex, secondIndex] = game.feedback.indices;
      const first = game.cards[firstIndex];
      const second = game.cards[secondIndex];
      if (first !== undefined && second !== undefined) {
        equation = `${first} ＋ ${second} ${game.feedback.kind === "correct" ? "＝" : "≠"} ${game.target}`;
      }
      feedbackText = game.feedback.kind === "correct"
        ? "○ せいかい！ そのちょうし！"
        : "△ おしい！ もういちど";
      feedbackClass = `is-${game.feedback.kind}`;
    }

    const cards = game.cards.map((value, index) => {
      const selected = game.selectedIndex === index;
      const feedback = game.feedback?.indices.includes(index) ? `is-${game.feedback.kind}` : "";
      return `
        <button
          class="number-card ${selected ? "is-selected" : ""} ${feedback}"
          type="button"
          data-card-index="${index}"
          aria-label="${value}のカード${selected ? "、えらんでいます" : ""}"
          aria-pressed="${selected}"
          aria-disabled="${this.inputLocked}"
          ${this.inputLocked ? "disabled" : ""}
        >${value}</button>
      `;
    }).join("");

    this.screens.game.innerHTML = `
      <div class="game-layout">
        <div class="top-bar">
          <button class="icon-button" id="leave-game" type="button" aria-label="ホームにもどる">‹</button>
          <div class="game-level">
            <span class="game-level-name">${difficulty.label}　${game.correctCount} / ${PAIRS_TO_CLEAR}</span>
            <div class="progress-track" aria-label="${PAIRS_TO_CLEAR}くみ中${game.correctCount}くみ正解">
              <div class="progress-fill" style="width: ${progress}%"></div>
            </div>
            <span class="elapsed-time" id="elapsed-time" aria-label="けいかじかん">⏱ ${formatRunningTime(performance.now() - this.startedAt)}</span>
          </div>
          <button class="icon-button" id="toggle-sound" type="button" aria-label="おとを${this.audio.isEnabled() ? "けす" : "だす"}">${this.audio.isEnabled() ? "♪" : "×"}</button>
        </div>
        <div class="target-area">
          <p class="target-prompt">この かずを つくろう</p>
          <div class="target-number ${this.showNewRound ? "is-new-round" : ""}" aria-label="つくる数は${game.target}">${game.target}</div>
        </div>
        <p class="equation-status" aria-live="polite">${equation}</p>
        <p class="feedback-line ${feedbackClass}" aria-live="assertive">${feedbackText}</p>
        <div class="number-board board-size-${game.cards.length}" aria-label="数字カード">${cards}</div>
        <p class="board-note">まちがい ${game.mistakeCount}かい　・　あと ${PAIRS_TO_CLEAR - game.correctCount}くみ</p>
      </div>
    `;

    this.screens.game.querySelectorAll<HTMLButtonElement>("[data-card-index]").forEach((button) => {
      button.addEventListener("click", () => {
        this.handleCardChoice(Number(button.dataset.cardIndex));
      });
    });
    requiredElement<HTMLButtonElement>("#leave-game").addEventListener("click", () => {
      this.openConfirmation(
        "ゲームを おわる？",
        "いまの きろくは のこらないよ。",
        "おわる",
        () => this.showHome(),
      );
    });
    requiredElement<HTMLButtonElement>("#toggle-sound").addEventListener("click", () => {
      this.toggleSound();
      this.renderGame();
    });
  }

  private handleCardChoice(index: number): void {
    if (!this.game || this.inputLocked) return;
    const previouslySelected = this.game.selectedIndex;
    this.game = chooseCard(this.game, index);

    if (this.game.phase === "playing") {
      if (previouslySelected !== index) this.audio.play("select");
      this.renderGame();
      return;
    }

    this.inputLocked = true;
    this.renderGame();
    const feedbackKind = this.game.feedback?.kind;
    const sessionId = this.sessionId;

    if (this.game.phase === "finished") {
      const elapsedMs = performance.now() - this.startedAt;
      this.stopTimer();
      this.audio.play("finish");
      window.setTimeout(() => {
        if (sessionId !== this.sessionId) return;
        this.finishGame(elapsedMs);
      }, 650);
      return;
    }

    if (feedbackKind) this.audio.play(feedbackKind);

    const feedbackDelay = feedbackKind === "correct" ? 560 : 650;
    window.setTimeout(() => {
      if (sessionId !== this.sessionId || !this.game) return;
      this.game = continueAfterFeedback(this.game);
      if (feedbackKind === "correct") {
        this.showNewRound = true;
        this.renderGame();
        window.setTimeout(() => {
          if (sessionId !== this.sessionId) return;
          this.showNewRound = false;
          this.inputLocked = false;
          this.renderGame();
        }, 300);
        return;
      }
      this.inputLocked = false;
      this.renderGame();
    }, feedbackDelay);
  }

  private finishGame(elapsedMs: number): void {
    if (!this.game) return;
    this.stopTimer();
    const candidate: BestRecord = {
      mistakeCount: this.game.mistakeCount,
      elapsedMs,
    };
    const isNewBest = this.storage.updateBest(this.game.difficultyId, candidate);
    this.result = {
      difficultyId: this.game.difficultyId,
      elapsedMs,
      mistakeCount: this.game.mistakeCount,
      isNewBest,
    };
    this.inputLocked = false;
    this.renderResult();
    this.showScreen("result");
  }

  private renderResult(): void {
    if (!this.result) return;
    const result = this.result;
    const difficulty = DIFFICULTIES[result.difficultyId];
    const best = this.storage.getSnapshot().bestRecords[result.difficultyId];

    this.screens.result.innerHTML = `
      <div class="result-layout">
        <div class="result-sparkles" aria-hidden="true"><span>✦</span><span>★</span><span>✦</span></div>
        <h2 id="result-title">できた！</h2>
        <p class="result-message">${difficulty.label}を クリアしたよ</p>
        ${result.isNewBest ? '<p class="new-best-badge">★ ベストきろく！</p>' : ""}
        <div class="result-cards">
          <div class="result-card">
            <span class="result-label">かかった じかん</span>
            <span class="result-value">${formatTime(result.elapsedMs)}</span>
          </div>
          <div class="result-card">
            <span class="result-label">まちがい</span>
            <span class="result-value">${result.mistakeCount}かい</span>
          </div>
        </div>
        <div class="best-summary">
          <span>この むずかしさの ベスト</span>
          <strong>${formatBestRecord(best)}</strong>
        </div>
        <div class="result-actions">
          <button class="primary-button" id="play-again" type="button">もういちど あそぶ</button>
          <button class="secondary-button" id="back-home" type="button">むずかしさを えらびなおす</button>
          <a class="secondary-button games-result-link" href="./games.html">ゲームいちらんへ</a>
        </div>
      </div>
    `;

    requiredElement<HTMLButtonElement>("#play-again").addEventListener("click", () => {
      this.startGame(result.difficultyId);
    });
    requiredElement<HTMLButtonElement>("#back-home").addEventListener("click", () => this.showHome());
  }

  private renderSettings(): void {
    this.screens.settings.innerHTML = `
      <div class="settings-layout">
        <div class="settings-header">
          <button class="icon-button" id="settings-back" type="button" aria-label="ホームにもどる">‹</button>
          <h2 id="settings-title">あそびかた・せってい</h2>
          <span class="header-spacer" aria-hidden="true"></span>
        </div>
        <div class="dialog-art settings-equation" aria-hidden="true">1 ＋ 4 ＝ 5</div>
        <ol class="how-to-list">
          <li><span>1</span><p>おおきな まるの<br>かずを みよう</p></li>
          <li><span>2</span><p>たすと ぴったりになる<br>カードを ふたつ えらぼう</p></li>
          <li><span>3</span><p>10くみの タイムアタック！<br>はやく みつけよう</p></li>
        </ol>
        <p class="dialog-note">まちがえても だいじょうぶ。なんどでも ためせるよ。</p>
        <div class="settings-panel">
          <div class="setting-row">
            <div><strong>おと</strong><span>こうかおんを だす</span></div>
            <button
              class="switch-button ${this.audio.isEnabled() ? "is-on" : ""}"
              id="settings-sound"
              type="button"
              role="switch"
              aria-checked="${this.audio.isEnabled()}"
            ><span aria-hidden="true"></span>${this.audio.isEnabled() ? "オン" : "オフ"}</button>
          </div>
          <button class="reset-button" id="reset-records" type="button">ベストきろくを けす</button>
        </div>
        <p class="privacy-note">なまえや ねんれいは ほぞんしません</p>
      </div>
    `;

    requiredElement<HTMLButtonElement>("#settings-back").addEventListener("click", () => this.showHome());
    requiredElement<HTMLButtonElement>("#settings-sound").addEventListener("click", () => {
      this.toggleSound();
      this.renderSettings();
    });
    requiredElement<HTMLButtonElement>("#reset-records").addEventListener("click", () => {
      this.openConfirmation(
        "ベストきろくを けす？",
        "3つの むずかしさの きろくが ぜんぶ きえるよ。",
        "けす",
        () => {
          this.storage.resetRecords();
          this.renderSettings();
          this.showToast("ベストきろくを けしました");
        },
      );
    });
  }

  private toggleSound(): void {
    const enabled = !this.audio.isEnabled();
    this.audio.setEnabled(enabled);
    this.storage.setSoundEnabled(enabled);
    if (enabled) this.audio.play("select");
  }

  private startTimer(): void {
    this.stopTimer();
    this.timerId = window.setInterval(() => this.updateTimerDisplay(), 250);
  }

  private stopTimer(): void {
    if (this.timerId === null) return;
    window.clearInterval(this.timerId);
    this.timerId = null;
  }

  private updateTimerDisplay(): void {
    const timer = this.screens.game.querySelector<HTMLElement>("#elapsed-time");
    if (!timer || !this.game) return;
    const elapsedMs = performance.now() - this.startedAt;
    timer.textContent = `⏱ ${formatRunningTime(elapsedMs)}`;
    timer.setAttribute("aria-label", `けいかじかん ${Math.floor(elapsedMs / 1000)}びょう`);
  }

  private showHome(): void {
    this.sessionId += 1;
    this.stopTimer();
    this.inputLocked = false;
    this.showNewRound = false;
    this.game = null;
    this.renderHome();
    this.showScreen("home");
  }

  private openConfirmation(
    title: string,
    message: string,
    acceptLabel: string,
    action: () => void,
  ): void {
    this.confirmTitle.textContent = title;
    this.confirmMessage.textContent = message;
    this.confirmAccept.textContent = acceptLabel;
    this.pendingConfirmation = action;
    this.confirmDialog.showModal();
  }

  private closeConfirmation(): void {
    this.pendingConfirmation = null;
    this.confirmDialog.close();
  }

  private showToast(message: string): void {
    this.toast.textContent = message;
    this.toast.classList.add("is-visible");
    if (this.toastTimer !== null) window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => {
      this.toast.classList.remove("is-visible");
      this.toastTimer = null;
    }, 2400);
  }
}
